import { landingAssets } from './landingAssets'
import type { ProductScreenKey } from './ProductScreenVisual'

export type LandingLocale = 'en' | 'ar' | 'tr' | 'fr'
export type TextPair = [string, string]

export type LandingMediaAsset = {
  type: 'image' | 'video' | 'gif'
  src: string
  alt: string
  poster?: string
}

export type ScreenCard = {
  title: string
  subtitle: string
  chips: string[]
  media: LandingMediaAsset
  visualKey: ProductScreenKey
}

export type AboutMissionCopy = {
  aboutTitle: string
  aboutCopy: string
  missionTitle: string
  missionCopy: string
}

export type SeoKeywordGroup = {
  label: string
  keywords: string[]
}

export type SeoLandingCopy = {
  eyebrow: string
  title: string
  copy: string
  featureColumn: string
  note: string
  keywords?: SeoKeywordGroup[]
}

export type ProblemSolutionUseCase = {
  segment: string
  visualKey: ProductScreenKey
  visualDescription: string
  aspectRatio: string
  problem: string
  fixedBy: string
  proofPoints: string[]
}

export type ComparisonFeature = {
  feature: string
  values: string[]
}

export type CompetitorGap = {
  vendor: string
  has: string
  nexusGap: string
}

export type ComparisonLandingCopy = {
  eyebrow: string
  title: string
  copy: string
  featureColumn: string
  vendors: string[]
  note: string
}

export type LandingContent = {
  nav: string[]
  signIn: string
  getStarted: string
  heroEyebrow: string
  heroTitle: string
  heroCopy: string
  heroProof: string[]
  overviewTitle: string
  overviewCopy: string
  screensTitle: string
  screensCopy: string
  useCasesTitle: string
  roiTitle: string
  featuresTitle: string
  howTitle: string
  finalTitle: string
  finalCopy: string
  finalCta: string
}

export type ContactLandingCopy = {
  eyebrow: string
  title: string
  copy: string
  fields: {
    name: string
    email: string
    company: string
    phone: string
    message: string
  }
  submit: string
  submitting: string
  success: string
  error: string
}

const withLandingFallbacks = <T,>(records: Record<'en' | 'ar', T> & Partial<Record<LandingLocale, T>>): Record<LandingLocale, T> => ({
  ...records,
  tr: records.tr ?? records.en,
  fr: records.fr ?? records.en,
})

export const content: Record<LandingLocale, LandingContent> = withLandingFallbacks({
  en: {
    nav: ['Product', 'Use Cases', 'Comparison', 'ROI', 'Contact'],
    signIn: 'Sign in',
    getStarted: 'Get started',
    heroEyebrow: 'Admin savings for agencies and companies',
    heroTitle: 'NexusWorkforce360 saves 4-5 admin hours per week across approvals, billing checks, and payroll prep.',
    heroCopy: 'Run schedules, geofenced attendance, jobs and orders, worker assignments, approvals, invoice validation, and export-ready timesheets across multi-organization staffing networks.',
    heroProof: ['260+ hours/year saved', 'Agency + client approvals', 'Payroll/export readiness'],
    overviewTitle: 'Built for real staffing operations',
    overviewCopy: 'NexusWorkforce360 connects companies, agencies, supervisors, and workers around the same operational truth. Admin teams post demand, assign workers, watch live coverage, review timesheets, validate billing, and prepare payroll exports without stitching together spreadsheets and chat threads.',
    screensTitle: 'Operational screens your teams can trust',
    screensCopy: 'Use NexusWorkforce360 from the web admin workspace and worker/supervisor mobile flows. These product views are generated from the active product surfaces: Admin jobs, live map coverage, and worker punch flows.',
    useCasesTitle: 'Use cases',
    roiTitle: 'ROI examples',
    featuresTitle: 'Feature grid',
    howTitle: 'How it works',
    finalTitle: 'Ready to see NexusWorkforce360 in action?',
    finalCopy: 'Start with agency operations, then invite client companies into shared approvals, billing visibility, and export-ready payroll workflows.',
    finalCta: 'Open NexusWorkforce360',
  },
  ar: {
    nav: ['المنتج', 'حالات الاستخدام', 'المقارنة', 'العائد', 'الشاشات'],
    signIn: 'تسجيل الدخول',
    getStarted: 'ابدأ الآن',
    heroEyebrow: 'توفير إداري للوكالات والشركات',
    heroTitle: 'يوفر NexusWorkforce360 من 4 إلى 5 ساعات إدارية أسبوعيا في الاعتمادات والتحقق من الفوترة وتحضير الرواتب.',
    heroCopy: 'أدر الجداول والحضور بالنطاق الجغرافي والوظائف والطلبات وتعيينات العمال والاعتمادات والتحقق من الفواتير وجداول الوقت الجاهزة للتصدير عبر شبكة متعددة المؤسسات.',
    heroProof: ['أكثر من 260 ساعة سنويا', 'اعتمادات مشتركة بين الوكالة والعميل', 'جاهزية الرواتب والتصدير'],
    overviewTitle: 'مصمم لعمليات التوظيف الواقعية',
    overviewCopy: 'يربط NexusWorkforce360 الشركات والوكالات والمشرفين والعمال حول مصدر تشغيلي واحد. تنشر فرق الإدارة الطلب، وتعين العمال، وتتابع التغطية المباشرة، وتراجع الجداول الزمنية، وتتحقق من الفوترة، وتجهز تصديرات الرواتب بدون الاعتماد على جداول ورسائل متفرقة.',
    screensTitle: 'شاشات تشغيلية موثوقة للفرق',
    screensCopy: 'استخدم NexusWorkforce360 من مساحة إدارة الويب وتدفقات العامل والمشرف على الهاتف. تعرض هذه الواجهات نوع لوحات التحكم الحية التي تحتاجها الفرق يوميا.',
    useCasesTitle: 'حالات الاستخدام',
    roiTitle: 'أمثلة العائد',
    featuresTitle: 'شبكة الميزات',
    howTitle: 'طريقة العمل',
    finalTitle: 'هل أنت جاهز لتجربة NexusWorkforce360؟',
    finalCopy: 'ابدأ بعمليات الوكالة، ثم ادع شركات العملاء إلى الاعتمادات المشتركة ورؤية الفوترة وسير الرواتب الجاهز للتصدير.',
    finalCta: 'افتح NexusWorkforce360',
  },
  tr: {
    nav: ['Ürün', 'Kullanım alanları', 'Karşılaştırma', 'ROI', 'Ekranlar'],
    signIn: 'Oturum aç',
    getStarted: 'Başlayın',
    heroEyebrow: 'Ajanslar ve şirketler için saha iş gücü operasyonları',
    heroTitle: 'NexusWorkforce360 her vardiyayı, pointajı, süpervizörü ve bordro aktarımını tek canlı operasyon görünümünde toplar.',
    heroCopy: 'Çok kurumlu personel ağlarında konum sınırına dayalı zaman takibi, işler ve siparişler, çalışan atamaları, onaylar ve dışa aktarıma hazır puantajlar yönetin.',
    heroProof: ['Canlı iş gücü görünürlüğü', 'Konum sınırıyla giriş/çıkış kaydı', 'Bordro ve dışa aktarım hazırlığı'],
    overviewTitle: 'Gerçek personel operasyonları için tasarlandı',
    overviewCopy: 'NexusWorkforce360 şirketleri, ajansları, süpervizörleri ve çalışanları aynı operasyon gerçeği etrafında birleştirir. Yönetim ekipleri talep yayınlar, çalışan atar, canlı kapsama bakar, puantajları inceler ve bordro dışa aktarımlarını hazırlar.',
    screensTitle: 'Ekiplerinizin güvenebileceği operasyon ekranları',
    screensCopy: 'NexusWorkforce360 web yönetim alanında ve çalışan/süpervizör mobil akışlarında kullanılabilir. Bu ürün görünümleri aktif ürün yüzeylerinden üretilmiştir.',
    useCasesTitle: 'Kullanım alanları',
    roiTitle: 'ROI örnekleri',
    featuresTitle: 'Özellikler',
    howTitle: 'Nasıl çalışır',
    finalTitle: 'NexusWorkforce360’ı çalışırken görmeye hazır mısınız?',
    finalCopy: 'Oturum açmak veya ilk ajans/şirket çalışma alanını oluşturmak için mevcut uygulama girişinden başlayın.',
    finalCta: 'NexusWorkforce360’ı aç',
  },
  fr: {
    nav: ['Produit', 'Cas d’usage', 'Comparaison', 'ROI', 'Écrans'],
    signIn: 'Se connecter',
    getStarted: 'Commencer',
    heroEyebrow: 'Économies administratives pour agences et entreprises',
    heroTitle: 'NexusWorkforce360 économise 4 à 5 heures admin par semaine sur les validations, contrôles de facturation et préparation paie.',
    heroCopy: 'Pilotez plannings, présence géolocalisée, postes et commandes, affectations, validations, contrôle facture et feuilles de temps prêtes à l’export dans des réseaux multi-organisations.',
    heroProof: ['260+ heures/an récupérées', 'Validations agence + client', 'Préparation paie et export'],
    overviewTitle: 'Conçu pour les vraies opérations de staffing',
    overviewCopy: 'NexusWorkforce360 relie entreprises, agences, superviseurs et employés autour de la même réalité opérationnelle. Les équipes admin publient la demande, affectent les employés, suivent la couverture en direct, valident les feuilles de temps, contrôlent la facturation et préparent les exports paie.',
    screensTitle: 'Des écrans opérationnels fiables pour vos équipes',
    screensCopy: 'Utilisez NexusWorkforce360 depuis l’espace admin web et les parcours mobiles employé/superviseur. Ces vues produit viennent des surfaces actives du produit.',
    useCasesTitle: 'Cas d’usage',
    roiTitle: 'Exemples de ROI',
    featuresTitle: 'Grille de fonctionnalités',
    howTitle: 'Fonctionnement',
    finalTitle: 'Prêt à voir NexusWorkforce360 en action ?',
    finalCopy: 'Commencez par les opérations agence, puis invitez les clients dans un workflow partagé de validation, visibilité facturation et paie prête à l’export.',
    finalCta: 'Ouvrir NexusWorkforce360',
  },
})

export const contactContent: Record<LandingLocale, ContactLandingCopy> = withLandingFallbacks({
  en: {
    eyebrow: 'Contact us',
    title: 'Book a NexusWorkforce360 demo',
    copy: 'Tell us about your workforce operation and we will follow up to schedule a practical product walkthrough.',
    fields: {
      name: 'Name',
      email: 'Email',
      company: 'Company',
      phone: 'Phone',
      message: 'Message',
    },
    submit: 'Book demo',
    submitting: 'Sending...',
    success: 'Thanks. Your request was sent.',
    error: 'We could not send the request. Please try again.',
  },
  ar: {
    eyebrow: 'تواصل معنا',
    title: 'اطلب عرضاً توضيحياً من رقيب 360',
    copy: 'أخبرنا عن تشغيل القوى العاملة لديك وسنتواصل معك لترتيب جولة عملية في المنتج.',
    fields: {
      name: 'الاسم',
      email: 'البريد الإلكتروني',
      company: 'الشركة',
      phone: 'الهاتف',
      message: 'الرسالة',
    },
    submit: 'أرسل طلب العرض',
    submitting: 'جار الإرسال...',
    success: 'شكرا لك. تم إرسال طلبك.',
    error: 'تعذر إرسال الطلب. يرجى المحاولة مرة أخرى.',
  },
  fr: {
    eyebrow: 'Contact',
    title: 'Réserver une démo NexusWorkforce360',
    copy: 'Parlez-nous de vos opérations workforce et nous reviendrons vers vous pour organiser une démonstration pratique du produit.',
    fields: {
      name: 'Nom',
      email: 'E-mail',
      company: 'Entreprise',
      phone: 'Téléphone',
      message: 'Message',
    },
    submit: 'Réserver une démo',
    submitting: 'Envoi...',
    success: 'Merci. Votre demande a été envoyée.',
    error: 'Impossible d’envoyer la demande. Veuillez réessayer.',
  },
})

export const useCases: Record<LandingLocale, TextPair[]> = withLandingFallbacks({
  en: [
    ['Agency dispatch', 'Fill open jobs faster by matching workers to orders, locations, skills, and availability.'],
    ['Company site operations', 'Give supervisors a live view of who is assigned, on site, late, on break, or missing.'],
    ['Shared staffing networks', 'Support multi-organization agency/company relationships without losing tenant boundaries.'],
    ['Payroll review', 'Approve timesheets with job, shift, punch, break, and location context before export.'],
  ],
  ar: [
    ['توزيع الوكالة', 'املأ الوظائف المفتوحة بسرعة عبر مطابقة العمال مع الطلبات والمواقع والمهارات والتوفر.'],
    ['تشغيل مواقع الشركات', 'امنح المشرفين رؤية مباشرة لمن تم تعيينه، ومن هو في الموقع، أو متأخر، أو في استراحة، أو غائب.'],
    ['شبكات توظيف مشتركة', 'ادعم علاقات الوكالات والشركات متعددة المؤسسات مع الحفاظ على حدود المستأجر.'],
    ['مراجعة الرواتب', 'اعتمد الجداول الزمنية مع سياق الوظيفة والمناوبة والتسجيل والاستراحات والموقع قبل التصدير.'],
  ],
  fr: [
    ['Répartition agence', 'Remplissez plus vite les postes ouverts en associant travailleurs, commandes, sites, compétences et disponibilités.'],
    ['Opérations site client', 'Donnez aux superviseurs une vue en direct des personnes affectées, présentes, en retard, en pause ou absentes.'],
    ['Réseaux de staffing partagés', 'Soutenez les relations agence/entreprise multi-organisations sans perdre les frontières tenant.'],
    ['Revue paie', 'Validez les feuilles de temps avec le contexte poste, shift, pointage, pause et site avant export.'],
  ],
})

export const roiExamples: Record<LandingLocale, TextPair[]> = withLandingFallbacks({
  en: [
    ['Recover 6-10 admin hours weekly', 'Replace manual punch reconciliation and spreadsheet-based shift checks with approval queues and export-ready records.'],
    ['Reduce site coverage gaps', 'Live supervisor visibility helps teams spot late arrivals, missed punches, and open slots before they become payroll or client issues.'],
    ['Shorten payroll close', 'Approved timesheets and structured exports reduce rework between operations, finance, agencies, and companies.'],
  ],
  ar: [
    ['استرجاع 6-10 ساعات إدارية أسبوعيا', 'استبدل مطابقة التسجيلات اليدوية وفحص المناوبات بالجداول بصفوف اعتماد وسجلات جاهزة للتصدير.'],
    ['تقليل فجوات التغطية في الموقع', 'تساعد الرؤية المباشرة للمشرفين على اكتشاف التأخير والتسجيلات الناقصة والخانات المفتوحة قبل تحولها لمشكلة عميل أو رواتب.'],
    ['تسريع إغلاق الرواتب', 'تقلل الجداول المعتمدة والتصديرات المنظمة إعادة العمل بين العمليات والمالية والوكالات والشركات.'],
  ],
  fr: [
    ['Récupérer 6 à 10 heures admin par semaine', 'Remplacez le rapprochement manuel des pointages et les contrôles de shifts par des files de validation et des enregistrements prêts à l’export.'],
    ['Réduire les trous de couverture site', 'La visibilité superviseur en direct aide à repérer retards, pointages manquants et postes ouverts avant qu’ils ne deviennent des problèmes client ou paie.'],
    ['Accélérer la clôture paie', 'Les feuilles de temps validées et les exports structurés réduisent les reprises entre opérations, finance, agences et entreprises.'],
  ],
})

export const features: Record<LandingLocale, string[]> = withLandingFallbacks({
  en: [
    'Workforce time tracking',
    'Geofenced punch-in/out',
    'Live supervisor visibility',
    'Jobs / Orders',
    'Worker assignments',
    'Timesheet approval',
    'Payroll/export readiness',
    'Multi-organization agency/company support',
  ],
  ar: [
    'تتبع وقت القوى العاملة',
    'تسجيل دخول وخروج بالنطاق الجغرافي',
    'رؤية مباشرة للمشرفين',
    'الوظائف / الطلبات',
    'تعيينات العمال',
    'اعتماد الجداول الزمنية',
    'جاهزية الرواتب والتصدير',
    'دعم متعدد المؤسسات للوكالات والشركات',
  ],
  fr: [
    'Suivi du temps workforce',
    'Pointage arrivée/départ géolocalisé',
    'Visibilité superviseur en direct',
    'Jobs / commandes',
    'Affectations travailleurs',
    'Validation des feuilles de temps',
    'Préparation paie/export',
    'Support multi-organisations agence/entreprise',
  ],
})

export const steps: Record<LandingLocale, TextPair[]> = withLandingFallbacks({
  en: [
    ['Create demand', 'Post jobs, orders, shifts, required headcount, locations, and rate context.'],
    ['Assign and monitor', 'Match workers, confirm assignments, and see live punch and coverage status.'],
    ['Approve and export', 'Review exceptions, approve timesheets, and prepare payroll or system exports.'],
  ],
  ar: [
    ['أنشئ الطلب', 'انشر الوظائف والطلبات والمناوبات وعدد العمال المطلوب والمواقع وسياق الأسعار.'],
    ['عيّن وراقب', 'طابق العمال، أكد التعيينات، وشاهد حالة التسجيل والتغطية مباشرة.'],
    ['اعتمد وصدّر', 'راجع الاستثناءات، اعتمد الجداول الزمنية، وجهز الرواتب أو تصديرات الأنظمة.'],
  ],
  fr: [
    ['Créer la demande', 'Publiez postes, commandes, shifts, effectifs requis, sites et contexte tarifaire.'],
    ['Affecter et suivre', 'Associez les travailleurs, confirmez les affectations et suivez pointages et couverture en direct.'],
    ['Valider et exporter', 'Revoyez les exceptions, validez les feuilles de temps et préparez paie ou exports système.'],
  ],
})

export const screenCards: Record<LandingLocale, ScreenCard[]> = withLandingFallbacks({
  en: [
    {
      title: 'Admin operations',
      subtitle: 'Jobs, assignments, timesheets, organizations',
      chips: ['Orders live', '87 assigned', '12 pending approval'],
      visualKey: 'jobs-orders',
      media: {
        ...landingAssets.adminOperations,
      },
    },
    {
      title: 'Supervisor view',
      subtitle: 'Location coverage, punch status, live workers',
      chips: ['18 on site', '2 late', '1 geofence warning'],
      visualKey: 'geofence-map',
      media: {
        ...landingAssets.supervisorMap,
      },
    },
    {
      title: 'Worker mobile',
      subtitle: 'Assigned shift, punch action, break status',
      chips: ['Next: 7:00 AM', 'Geofence ready', 'Timesheet submitted'],
      visualKey: 'worker-mobile',
      media: {
        ...landingAssets.workerMobile,
      },
    },
  ],
  ar: [
    {
      title: 'عمليات الإدارة',
      subtitle: 'الوظائف والتعيينات والجداول والمؤسسات',
      chips: ['طلبات مباشرة', '87 معين', '12 بانتظار الاعتماد'],
      visualKey: 'jobs-orders',
      media: {
        ...landingAssets.adminOperations,
        alt: 'شاشة عمليات الإدارة في NexusWorkforce360',
      },
    },
    {
      title: 'عرض المشرف',
      subtitle: 'تغطية الموقع وحالة التسجيل والعمال المباشرون',
      chips: ['18 في الموقع', '2 متأخر', '1 تنبيه نطاق'],
      visualKey: 'geofence-map',
      media: {
        ...landingAssets.supervisorMap,
        alt: 'خريطة المشرف في NexusWorkforce360',
      },
    },
    {
      title: 'هاتف العامل',
      subtitle: 'المناوبة المعينة وإجراء التسجيل وحالة الاستراحة',
      chips: ['التالي: 7:00 صباحا', 'النطاق جاهز', 'تم إرسال الجدول'],
      visualKey: 'worker-mobile',
      media: {
        ...landingAssets.workerMobile,
        alt: 'تدفق هاتف العامل في NexusWorkforce360',
      },
    },
  ],
  fr: [
    {
      title: 'Opérations admin',
      subtitle: 'Jobs, affectations, feuilles de temps, organisations',
      chips: ['Commandes en direct', '87 affectés', '12 validations en attente'],
      visualKey: 'jobs-orders',
      media: {
        ...landingAssets.adminOperations,
        alt: 'Écran opérations admin dans NexusWorkforce360',
      },
    },
    {
      title: 'Vue superviseur',
      subtitle: 'Couverture site, statut pointage, travailleurs en direct',
      chips: ['18 sur site', '2 en retard', '1 alerte géofence'],
      visualKey: 'geofence-map',
      media: {
        ...landingAssets.supervisorMap,
        alt: 'Carte superviseur dans NexusWorkforce360',
      },
    },
    {
      title: 'Mobile travailleur',
      subtitle: 'Shift affecté, action de pointage, statut pause',
      chips: ['Suivant : 7 h 00', 'Géofence prête', 'Feuille envoyée'],
      visualKey: 'worker-mobile',
      media: {
        ...landingAssets.workerMobile,
        alt: 'Parcours mobile travailleur dans NexusWorkforce360',
      },
    },
  ],
})

export const aboutMission: Record<LandingLocale, AboutMissionCopy> = withLandingFallbacks({
  en: {
    aboutTitle: 'About NexusWorkforce360',
    aboutCopy: 'NexusWorkforce360 was built for organizations that manage people outside a single office: construction sites, security posts, facility teams, staffing agencies, multi-location operators, and subcontractor networks. Instead of forcing operations teams to reconcile spreadsheets, gate logs, access cards, mobile punches, WhatsApp updates, and payroll files, NexusWorkforce360 connects attendance, assignments, documents, approvals, and exports into one operational view.',
    missionTitle: 'Mission',
    missionCopy: 'Our mission is to make field workforce operations verifiable, auditable, and easier to run. Every shift should answer the questions that matter: who was assigned, who arrived, where they punched, whether they were eligible, who approved the hours, and what is ready for payroll or billing.',
  },
  ar: {
    aboutTitle: 'عن NexusWorkforce360',
    aboutCopy: 'تم تصميم NexusWorkforce360 للمؤسسات التي تدير فرق عمل خارج مكتب واحد: مواقع المقاولات، نقاط الحراسة، فرق المرافق، وكالات التوظيف، العمليات متعددة الفروع، وشبكات المقاولين الفرعيين. بدلا من إجبار فرق التشغيل على مطابقة الجداول وسجلات البوابات وبطاقات الدخول وتسجيلات الهاتف ورسائل واتساب وملفات الرواتب، يربط NexusWorkforce360 الحضور والتعيينات والمستندات والاعتمادات والتصدير في عرض تشغيلي واحد.',
    missionTitle: 'المهمة',
    missionCopy: 'مهمتنا هي جعل تشغيل القوى العاملة الميدانية قابلا للتحقق والمراجعة وأسهل في الإدارة. يجب أن تجيب كل مناوبة على الأسئلة المهمة: من تم تعيينه، من وصل، أين تم تسجيل الحضور، هل كان مؤهلا، من اعتمد الساعات، وما الجاهز للرواتب أو الفوترة.',
  },
})

export const seoIntent: Record<LandingLocale, SeoLandingCopy> = withLandingFallbacks({
  en: {
    eyebrow: 'Search intent',
    title: 'Built for the searches buyers actually make',
    copy: 'NexusWorkforce360 landing content targets high-intent searches around geofencing attendance, construction time tracking, workforce management, subcontractor tracking, payroll-ready timesheets, and GCC worker compliance.',
    featureColumn: 'Feature',
    note: 'Competitor capabilities and pricing should be rechecked before publishing final sales collateral. This comparison is positioned for buyer education and SEO, not as a legal claim of exhaustive parity.',
    keywords: [{
      label: 'Buyer searches',
      keywords: ['Geofencing attendance app', 'GPS attendance app', 'Construction time tracking software', 'Construction workforce management software', 'Jobsite attendance tracking', 'Subcontractor tracking software', 'Payroll-ready timesheet software', 'Worker document compliance software', 'Time attendance software Saudi Arabia', 'Workforce management software GCC', 'Mobile time clock with GPS', 'Site access compliance software'],
    }],
  },
  ar: {
    eyebrow: 'نية البحث',
    title: 'مصمم لعبارات البحث التي يستخدمها المشترون فعلا',
    copy: 'يستهدف محتوى NexusWorkforce360 عبارات بحث عالية النية حول الحضور بالنطاق الجغرافي، تتبع وقت المقاولات، إدارة القوى العاملة، تتبع المقاولين الفرعيين، الجداول الجاهزة للرواتب، وامتثال مستندات العمال في الخليج.',
    featureColumn: 'الميزة',
    note: 'يجب إعادة التحقق من قدرات وأسعار المنافسين قبل نشر مواد البيع النهائية. هذه المقارنة موجهة للتوعية والظهور في البحث وليست مطالبة قانونية شاملة.',
    keywords: [{
      label: 'عبارات بحث المشترين',
      keywords: ['تطبيق حضور وانصراف GPS', 'نظام حضور وانصراف بالنطاق الجغرافي', 'برنامج تتبع العمال في مواقع العمل', 'برنامج إدارة عمال المقاولات', 'تتبع حضور العمال في المشاريع', 'برنامج تتبع المقاولين الفرعيين', 'برنامج جداول زمنية للرواتب', 'إدارة مستندات العمال', 'برنامج حضور وانصراف في السعودية', 'إدارة القوى العاملة في الخليج', 'تطبيق تسجيل وقت بالموقع', 'إدارة تصاريح دخول الموقع'],
    }],
  },
})

export const problemSolutionUseCases: Record<LandingLocale, ProblemSolutionUseCase[]> = withLandingFallbacks({
  en: [
    ['Construction projects', 'geofence-map', 'Live Locations & Geofences view showing site radius, workers on site, warnings, and supervisor review context', '16:10', 'Paper timesheets, gate scans, bus departure records, and site checks rarely line up. Project teams lose time proving who was actually on site, which subcontractor supplied them, and which hours should be approved.', 'NexusWorkforce360 links worker assignments, site geofences, live punches, supervisor visibility, document readiness, and approval-ready timesheets to the same job and location.', ['Geofenced punch-in/out', 'Job and shift assignment context', 'Supplier/company relationship visibility', 'Timesheet approval and export readiness']],
    ['Security firms', 'geofence-map', 'Supervisor map with guards assigned to posts, late/missing signals, and geofence exception review', '16:10', 'Guards rotate across posts, sites, and shifts. Missed attendance, late arrivals, and unclear replacements become client service issues fast.', 'NexusWorkforce360 gives supervisors live post coverage, punch status, late/missing indicators, and shift assignment records across locations.', ['Live supervisor map', 'Location-scoped workers', 'Late/missing visibility', 'Shift and post assignment history']],
    ['Facility management', 'schedule-board', 'Schedule board showing multi-location shifts, unfilled coverage, and manager handoff state', '16:10', 'Cleaning, maintenance, and support teams move across towers, malls, hospitals, schools, and compounds. Managers need proof of presence without chasing every supervisor manually.', 'NexusWorkforce360 combines mobile GPS attendance, geofences, organization/location scopes, and export-ready timesheets for distributed teams.', ['Multi-location coverage', 'Mobile worker punch flows', 'Supervisor location access', 'Payroll-ready reporting']],
    ['Staffing agencies', 'network-relationships', 'Network Relationships screen showing agency-to-company sharing and granted location scopes', '4:3', 'Agencies need to supply workers to multiple companies while keeping control of worker records, documents, availability, and billable hours.', 'NexusWorkforce360 supports agency/company relationships, worker sharing, open shifts, worker availability, job assignments, and approval workflows.', ['Agency-company relationships', 'Worker sharing', 'Open-shift marketplace', 'Worker document readiness']],
    ['Retail and QSR multi-location operators', 'schedule-board', 'Branch schedule board with shift coverage, worker status, and open applicant review', '16:10', 'Branch managers need reliable attendance and shift coverage without managing everything through spreadsheets and chat groups.', 'NexusWorkforce360 helps schedule workers, validate location-based punches, track coverage, and prepare clean timesheet approvals across locations.', ['Multi-branch schedules', 'GPS punch validation', 'Manager approval queue', 'Timesheet export']],
    ['GCC subcontractor-heavy operations', 'worker-documents', 'Worker Documents screen showing ID, safety induction, site access, expiry, and assignment eligibility status', '4:3', 'Operations teams need to know not only who arrived, but whether that worker belongs to the right supplier, has valid documents, and is eligible for the assigned site.', 'NexusWorkforce360 connects supplier relationships, site assignments, geofences, GCC document readiness, approvals, and payroll/export rows.', ['Iqama, work permit, passport, safety induction support', 'Site access and medical clearance tracking', 'Supplier/prime relationship visibility', 'SAR payroll/export demo readiness']],
  ].map(([segment, visualKey, visualDescription, aspectRatio, problem, fixedBy, proofPoints]) => ({
    segment: segment as string,
    visualKey: visualKey as ProductScreenKey,
    visualDescription: visualDescription as string,
    aspectRatio: aspectRatio as string,
    problem: problem as string,
    fixedBy: fixedBy as string,
    proofPoints: proofPoints as string[],
  })),
  ar: [
    ['مشاريع المقاولات', 'geofence-map', 'شاشة المواقع والنطاقات تعرض نطاق الموقع والعمال الموجودين والتنبيهات وسياق مراجعة المشرف', '16:10', 'الجداول الورقية وسجلات البوابة وحركة النقل وفحص الموقع لا تتطابق دائما. تضيع فرق المشاريع وقتا في إثبات من كان في الموقع ومن المورد وما الساعات التي يجب اعتمادها.', 'يربط NexusWorkforce360 التعيينات ونطاقات المواقع والتسجيلات المباشرة ورؤية المشرف وجاهزية المستندات والجداول الجاهزة للاعتماد بنفس الوظيفة والموقع.', ['تسجيل حضور وانصراف بالنطاق الجغرافي', 'سياق الوظيفة والمناوبة', 'رؤية علاقة المورد والشركة', 'اعتماد الجداول وجاهزية التصدير']],
    ['شركات الأمن', 'geofence-map', 'خريطة مشرف تعرض الحراس المعينين على النقاط مع حالات التأخير والغياب ومراجعة استثناءات النطاق', '16:10', 'يتنقل الحراس بين النقاط والمواقع والمناوبات. يتحول الغياب أو التأخير أو بديل غير واضح بسرعة إلى مشكلة خدمة للعميل.', 'يعطي NexusWorkforce360 المشرفين رؤية مباشرة لتغطية النقاط وحالة التسجيل ومؤشرات التأخير أو الغياب وسجل تعيين المناوبات عبر المواقع.', ['خريطة مشرف مباشرة', 'عمال مرتبطون بالموقع', 'رؤية التأخير والغياب', 'سجل تعيين النقاط والمناوبات']],
    ['إدارة المرافق', 'schedule-board', 'لوحة جدولة متعددة المواقع تعرض المناوبات غير المكتملة وحالة التغطية وتسليم المدير', '16:10', 'تتحرك فرق التنظيف والصيانة والدعم بين الأبراج والمجمعات والمستشفيات والمدارس. يحتاج المديرون إلى إثبات الوجود دون ملاحقة كل مشرف يدويا.', 'يجمع NexusWorkforce360 حضور الهاتف عبر GPS والنطاقات الجغرافية وصلاحيات المؤسسة والموقع والجداول الجاهزة للتصدير للفرق الموزعة.', ['تغطية متعددة المواقع', 'تدفقات تسجيل هاتفية', 'وصول المشرف حسب الموقع', 'تقارير جاهزة للرواتب']],
    ['وكالات التوظيف', 'network-relationships', 'شاشة علاقات الشبكة تعرض مشاركة الوكالة مع الشركة ونطاقات المواقع الممنوحة', '4:3', 'تحتاج الوكالات إلى تزويد عدة شركات بالعمال مع الحفاظ على سجلات العمال ومستنداتهم وتوفرهم وساعاتهم القابلة للفوترة.', 'يدعم NexusWorkforce360 علاقات الوكالة والشركة ومشاركة العمال والمناوبات المفتوحة وتوفر العمال وتعيينات الوظائف وسير الاعتماد.', ['علاقات الوكالة والشركة', 'مشاركة العمال', 'سوق مناوبات داخلي', 'جاهزية مستندات العمال']],
    ['مشغلو التجزئة والمطاعم متعددة الفروع', 'schedule-board', 'لوحة جدول الفروع تعرض تغطية المناوبات وحالة العمال ومراجعة المتقدمين للمناوبات المفتوحة', '16:10', 'يحتاج مديرو الفروع إلى حضور وتغطية مناوبات موثوقة دون إدارة كل شيء عبر الجداول ومجموعات الدردشة.', 'يساعد NexusWorkforce360 على جدولة العمال والتحقق من التسجيل حسب الموقع وتتبع التغطية وتجهيز اعتمادات جداول نظيفة عبر المواقع.', ['جداول متعددة الفروع', 'تحقق GPS للتسجيل', 'قائمة اعتماد المدير', 'تصدير الجداول']],
    ['عمليات خليجية كثيفة المقاولين الفرعيين', 'worker-documents', 'شاشة مستندات العامل تعرض الهوية وتعريف السلامة وبطاقة دخول الموقع والانتهاء وأهلية التعيين', '4:3', 'تحتاج فرق التشغيل إلى معرفة من وصل، وهل يتبع العامل المورد الصحيح، وهل مستنداته صالحة، وهل هو مؤهل للموقع المعين.', 'يربط NexusWorkforce360 علاقات الموردين وتعيينات المواقع والنطاقات الجغرافية وجاهزية مستندات الخليج والاعتمادات وصفوف الرواتب والتصدير.', ['دعم الإقامة وتصريح العمل والجواز وتعريف السلامة', 'تتبع دخول الموقع واللياقة الطبية', 'رؤية علاقة المورد والمقاول الرئيسي', 'جاهزية تصدير رواتب SAR للتجربة']],
  ].map(([segment, visualKey, visualDescription, aspectRatio, problem, fixedBy, proofPoints]) => ({
    segment: segment as string,
    visualKey: visualKey as ProductScreenKey,
    visualDescription: visualDescription as string,
    aspectRatio: aspectRatio as string,
    problem: problem as string,
    fixedBy: fixedBy as string,
    proofPoints: proofPoints as string[],
  })),
})

export const comparisonFeatures: Record<LandingLocale, ComparisonFeature[]> = withLandingFallbacks({
  en: [
    ['GPS attendance and geofencing', ['Yes', 'Yes', 'Yes', 'Yes', 'Yes', 'Partial']],
    ['Construction and field workforce focus', ['Strong', 'Strong', 'Strong', 'Generic', 'Strong', 'HR-first']],
    ['Agency and company relationship model', ['Native', 'No', 'No', 'No', 'Limited', 'Limited']],
    ['Worker sharing between organizations', ['Native', 'No', 'No', 'No', 'No', 'No']],
    ['Worker document and certificate readiness', ['Native', 'Limited', 'Limited', 'No', 'Yes', 'HR docs']],
    ['GCC documents: Iqama, permits, site access', ['Designed for GCC', 'No', 'No', 'No', 'Configurable', 'HR docs']],
    ['Live worker map and breadcrumbs', ['Yes', 'GPS trail', 'GPS trail', 'Partial', 'Partial', 'No']],
    ['Timesheet approval and payroll export', ['Yes', 'Yes', 'Yes', 'Yes', 'Yes', 'Yes']],
    ['Internal worker marketplace and availability', ['Yes', 'No', 'No', 'No', 'No', 'No']],
    ['Self-hosted deployment option', ['Yes', 'No', 'No', 'No', 'Ask vendor', 'No']],
  ].map(([feature, values]) => ({ feature: feature as string, values: values as string[] })),
  ar: [
    ['حضور GPS والنطاق الجغرافي', ['نعم', 'نعم', 'نعم', 'نعم', 'نعم', 'جزئي']],
    ['تركيز المقاولات والقوى العاملة الميدانية', ['قوي', 'قوي', 'قوي', 'عام', 'قوي', 'موارد بشرية أولا']],
    ['نموذج علاقة الوكالة والشركة', ['أصلي', 'لا', 'لا', 'لا', 'محدود', 'محدود']],
    ['مشاركة العمال بين المؤسسات', ['أصلي', 'لا', 'لا', 'لا', 'لا', 'لا']],
    ['جاهزية مستندات وشهادات العمال', ['أصلي', 'محدود', 'محدود', 'لا', 'نعم', 'مستندات HR']],
    ['مستندات الخليج: إقامة، تصاريح، دخول موقع', ['مصمم للخليج', 'لا', 'لا', 'لا', 'قابل للتهيئة', 'مستندات HR']],
    ['خريطة العمال المباشرة والمسارات', ['نعم', 'مسار GPS', 'مسار GPS', 'جزئي', 'جزئي', 'لا']],
    ['اعتماد الجداول وتصدير الرواتب', ['نعم', 'نعم', 'نعم', 'نعم', 'نعم', 'نعم']],
    ['سوق عمال داخلي والتوفر', ['نعم', 'لا', 'لا', 'لا', 'لا', 'لا']],
    ['خيار نشر ذاتي الاستضافة', ['نعم', 'لا', 'لا', 'لا', 'اسأل المورد', 'لا']],
  ].map(([feature, values]) => ({ feature: feature as string, values: values as string[] })),
  fr: [
    ['Présence GPS et géofencing', ['Oui', 'Oui', 'Oui', 'Oui', 'Oui', 'Partiel']],
    ['Focus construction et workforce terrain', ['Fort', 'Fort', 'Fort', 'Générique', 'Fort', 'RH d’abord']],
    ['Modèle relation agence/entreprise', ['Natif', 'Non', 'Non', 'Non', 'Limité', 'Limité']],
    ['Partage de travailleurs entre organisations', ['Natif', 'Non', 'Non', 'Non', 'Non', 'Non']],
    ['Readiness documents et certificats travailleurs', ['Natif', 'Limité', 'Limité', 'Non', 'Oui', 'Docs RH']],
    ['Documents GCC : Iqama, permis, accès site', ['Conçu pour le GCC', 'Non', 'Non', 'Non', 'Configurable', 'Docs RH']],
    ['Carte travailleurs en direct et traces', ['Oui', 'Trace GPS', 'Trace GPS', 'Partiel', 'Partiel', 'Non']],
    ['Validation feuilles de temps et export paie', ['Oui', 'Oui', 'Oui', 'Oui', 'Oui', 'Oui']],
    ['Marketplace interne travailleurs et disponibilité', ['Oui', 'Non', 'Non', 'Non', 'Non', 'Non']],
    ['Option déploiement auto-hébergé', ['Oui', 'Non', 'Non', 'Non', 'Demander au vendeur', 'Non']],
  ].map(([feature, values]) => ({ feature: feature as string, values: values as string[] })),
})

export const comparisonContent: Record<LandingLocale, ComparisonLandingCopy> = withLandingFallbacks({
  en: {
    eyebrow: 'Product comparison',
    title: 'Honest comparison for field workforce buyers',
    copy: 'NexusWorkforce360 is not just a time clock. It is a workforce operations layer for teams that need attendance, assignments, documents, worker sharing, and payroll-ready approvals in one tenant-aware workflow.',
    featureColumn: 'Feature',
    vendors: ['NexusWorkforce360', 'ClockShark', 'busybusy', 'QuickBooks Time', 'Truein', 'ZenHR'],
    note: 'Competitor capabilities and pricing should be rechecked before publishing final sales collateral. This comparison is positioned for buyer education and SEO, not as a legal claim of exhaustive parity.',
  },
  ar: {
    eyebrow: 'مقارنة المنتج',
    title: 'مقارنة واضحة لمشتري تشغيل القوى العاملة الميدانية',
    copy: 'تظهر قوة NexusWorkforce360 عندما تحتاج العمليات الميدانية إلى الحضور والتعيينات والمستندات ومشاركة العمال والاعتمادات الجاهزة للرواتب في سير عمل واحد معزول حسب المستأجر.',
    featureColumn: 'الميزة',
    vendors: ['NexusWorkforce360', 'ClockShark', 'busybusy', 'QuickBooks Time', 'Truein', 'ZenHR'],
    note: 'يجب إعادة التحقق من قدرات وأسعار المنافسين قبل نشر مواد البيع النهائية. هذه المقارنة موجهة للتوعية والظهور في البحث وليست مطالبة قانونية شاملة.',
  },
  fr: {
    eyebrow: 'Comparaison produit',
    title: 'Comparaison claire pour les acheteurs workforce terrain',
    copy: 'NexusWorkforce360 n’est pas seulement une pointeuse. C’est une couche d’opérations workforce pour les équipes qui ont besoin de présence, affectations, documents, partage de travailleurs et validations prêtes pour la paie dans un workflow tenant-aware.',
    featureColumn: 'Fonctionnalité',
    vendors: ['NexusWorkforce360', 'ClockShark', 'busybusy', 'QuickBooks Time', 'Truein', 'ZenHR'],
    note: 'Les capacités et prix concurrents doivent être revérifiés avant publication finale des supports commerciaux. Cette comparaison sert l’éducation acheteur et le SEO, pas une revendication juridique exhaustive.',
  },
})

export const competitorGaps: Record<LandingLocale, CompetitorGap[]> = withLandingFallbacks({
  en: [],
  ar: [],
})
